/** combinedViewer.ts */
import { useEffect } from 'react';
import { useRecoilValue } from 'recoil';
import { Mesh, Material, Object3DEventMap, SphereGeometry } from 'three';
import { ConvexGeometry } from 'three/examples/jsm/geometries/ConvexGeometry';
import { isVisibleMorphingBoxState } from 'features/three-view/state/threeAtoms';
import { createConvexMesh, createPointMeshes } from '../utils/geometry';
import { convexMaterial, selectedConvexMaterial, pointsMaterial, selectedPointsMaterial } from '../utils/materials';
import { useBoxViewerData } from '../hooks/useBoxViewerData';

export const MorphingBoxViewer: React.FC = () => {
  const isVisibleMorphingBox = useRecoilValue(isVisibleMorphingBoxState);
  const {
    scene,
    drawable,
    selectable,
    coordinates,
    selectedCoordinates,
    remainder
  } = useBoxViewerData();

  useEffect(() => {
    if (!drawable || !isVisibleMorphingBox) return;

    // Arrays to hold meshes for cleanup
    let meshes: Mesh<ConvexGeometry | SphereGeometry, Material, Object3DEventMap>[] = [];

    // Create convex meshes
    if (selectable) {
      if (selectedCoordinates.length > 0) {
        const selectedConvexMesh = createConvexMesh(selectedCoordinates, selectedConvexMaterial);
        if (selectedConvexMesh) {
          selectedConvexMesh.renderOrder = 1;
          scene.add(selectedConvexMesh);
          meshes.push(selectedConvexMesh);
        }
      }
    }
    const fullConvexMesh = createConvexMesh(coordinates, convexMaterial);
    if (fullConvexMesh) {
      fullConvexMesh.renderOrder = 0;
      scene.add(fullConvexMesh);
      meshes.push(fullConvexMesh);
    }

    // Create point meshes
    if (selectable) {
      if (selectedCoordinates.length > 0) {
        const selectedPointMeshes = createPointMeshes(selectedCoordinates, selectedPointsMaterial);
        selectedPointMeshes.forEach(pm => scene.add(pm));
        meshes.push(...selectedPointMeshes);
      }

      if (remainder.length > 0) {
        const remainderMeshes = createPointMeshes(remainder, pointsMaterial);
        remainderMeshes.forEach(pm => scene.add(pm));
        meshes.push(...remainderMeshes);
      }
    } else {
      const allPointMeshes = createPointMeshes(coordinates, pointsMaterial);
      allPointMeshes.forEach(pm => scene.add(pm));
      meshes.push(...allPointMeshes);
    }

    // Cleanup function to remove and dispose of meshes
    return () => {
      meshes.forEach(mesh => {
        scene.remove(mesh);
        mesh.geometry.dispose();
        mesh.material.dispose();
      });
    };
  }, [
    isVisibleMorphingBox,
    coordinates,
    selectedCoordinates,
    remainder,
    scene,
    drawable,
    selectable
  ]);

  return null;
};
